import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { sessionRoomApi } from "../api/sessionRoomApi";
import { normalizeMalformedAegisUrl } from "../utils/aegisUrl";
import { useAuth } from "../auth/authStore";
import logo from "../assets/img/logo.svg";

function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 3v3M17 3v3M4 9h16M6.2 5.5h11.6A2.7 2.7 0 0 1 20.5 8.2v9.6a2.7 2.7 0 0 1-2.7 2.7H6.2a2.7 2.7 0 0 1-2.7-2.7V8.2a2.7 2.7 0 0 1 2.7-2.7Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9.6 14.4 14.4 9.6M8 16a4 4 0 0 1 0-5.7l2.3-2.3a4 4 0 1 1 5.7 5.7l-.8.8M16 8a4 4 0 0 1 0 5.7l-2.3 2.3a4 4 0 1 1-5.7-5.7l.8-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7.5 10V8.25a4.5 4.5 0 1 1 9 0V10M6.75 10h10.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5v-6A1.5 1.5 0 0 1 6.75 10Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUserPlaceholder() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5ZM5.5 19.5a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function parseDate(raw) {
  if (!raw) return null;
  const value = new Date(raw);
  return Number.isNaN(value.getTime()) ? null : value;
}

function formatDateLabel(raw) {
  const value = parseDate(raw);
  if (!value) return "Дата уточняется";
  const parts = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(value);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const weekday = String(map.weekday || "").trim();
  const day = String(map.day || "").trim();
  const month = String(map.month || "").trim();
  const hour = String(map.hour || "").trim();
  const minute = String(map.minute || "").trim();
  if (!day || !month) return "Дата уточняется";
  return `${day} ${month}${weekday ? `, ${weekday}` : ""}${hour && minute ? ` · ${hour}:${minute}` : ""}`;
}

function buildGuestJoinPayload(name, email) {
  const guestName = String(name || "").trim();
  const guestEmail = String(email || "").trim().toLowerCase();
  const externalId = `guest:${guestEmail || guestName || "unknown"}`.slice(0, 80);
  return {
    display_name: guestName,
    email: guestEmail,
    external_user_id: externalId,
    audience: "external_guest",
    ttl_hours: 24,
    return_embed_url: true,
  };
}

function sanitizeInviteIdentityParams(rawUrl) {
  const value = normalizeMalformedAegisUrl(rawUrl);
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    const hasInviteToken = url.searchParams.has("invite_token") || url.searchParams.has("invite") || url.searchParams.has("token");
    if (!hasInviteToken) return url.toString();
    [
      "session_token",
      "participant_session_token",
      "pst",
      "participant_id",
      "user_id",
      "role",
    ].forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch {
    return value;
  }
}

function toGuestInviteUrl(token) {
  if (!token || typeof window === "undefined") return "";
  return `${window.location.origin}/conference/room/${encodeURIComponent(token)}${window.location.search || ""}`;
}

function resolveTokenFromRoute(params, pathname = "") {
  const direct = String(params?.roomToken || "").trim();
  if (direct) return direct;
  const wildcard = String(params?.["*"] || "").trim();
  if (wildcard) {
    const parts = wildcard.split("/").map((part) => String(part || "").trim()).filter(Boolean);
    if (parts.length === 1) return decodeURIComponent(parts[0]);
    if (parts.length >= 2 && parts[0].toLowerCase() === "room") return decodeURIComponent(parts[1]);
    return decodeURIComponent(parts[parts.length - 1]);
  }
  const path = String(pathname || "").trim();
  if (!path) return "";
  const match = path.match(/^\/conference\/(?:room\/)?([^/?#]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

function readGuestDraft(roomToken) {
  if (typeof window === "undefined") return null;
  const token = String(roomToken || "").trim();
  if (!token) return null;
  try {
    const raw = window.sessionStorage.getItem(`conference-guest:${token}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      name: String(parsed.name || "").trim(),
      email: String(parsed.email || "").trim().toLowerCase(),
    };
  } catch {
    return null;
  }
}

function persistGuestDraft(roomToken, draft) {
  if (typeof window === "undefined") return;
  const token = String(roomToken || "").trim();
  if (!token) return;
  try {
    window.sessionStorage.setItem(`conference-guest:${token}`, JSON.stringify({
      name: String(draft?.name || "").trim(),
      email: String(draft?.email || "").trim().toLowerCase(),
    }));
  } catch {
    // ignore
  }
}

export default function ConferenceGuestRoom() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const normalizedToken = React.useMemo(
    () => String(resolveTokenFromRoute(params, location.pathname) || "").trim(),
    [location.pathname, params]
  );

  const savedGuest = React.useMemo(() => readGuestDraft(normalizedToken), [normalizedToken]);
  const [guestName, setGuestName] = React.useState(savedGuest?.name || "");
  const [guestEmail, setGuestEmail] = React.useState(savedGuest?.email || "");
  const [submitting, setSubmitting] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");
  const [room, setRoom] = React.useState(null);
  const [joined, setJoined] = React.useState(false);
  const [inviteCopied, setInviteCopied] = React.useState(false);

  const roomTitle = String(room?.title || room?.sessionTitle || room?.groupTitle || "").trim() || "Онлайн-конференция";
  const startAt = room?.sessionStartAt || room?.startAt || room?.scheduledAt || room?.startDateTime || "";
  const psychologistName = String(room?.psychologistName || room?.psychologist?.name || room?.therapistName || "").trim();
  const psychologistAvatarUrl = String(
    room?.psychologistAvatarUrl
    || room?.psychologist_avatar_url
    || room?.psychologist?.avatarUrl
    || room?.psychologist?.avatar_url
    || room?.psychologist?.avatar
    || ""
  ).trim();
  const sessionLabel = room?.group ? "Групповая встреча" : "Онлайн-консультация";
  const cabinetLink = `/dashboard/conferences/room/${encodeURIComponent(normalizedToken)}`;

  const playerUrl = React.useMemo(
    () => sanitizeInviteIdentityParams(room?.aegisJoinUrl || room?.aegisPlayerUrl || ""),
    [room]
  );

  const loginFrom = `${location.pathname}${location.search || ""}${location.hash || ""}`;

  React.useEffect(() => {
    let alive = true;
    if (!normalizedToken) {
      setLoadError("Ссылка конференции некорректна");
      return () => {
        alive = false;
      };
    }

    setLoadError("");
    sessionRoomApi
      .getPublicRoom(normalizedToken)
      .catch(async () => {
        try {
          return await sessionRoomApi.getRoom(normalizedToken);
        } catch {
          return null;
        }
      })
      .then((nextRoom) => {
        if (!alive || !nextRoom) return;
        setRoom(nextRoom);
      });

    return () => {
      alive = false;
    };
  }, [normalizedToken]);

  React.useEffect(() => {
    persistGuestDraft(normalizedToken, { name: guestName, email: guestEmail });
  }, [guestEmail, guestName, normalizedToken]);

  const onJoinAsGuest = React.useCallback(async (event) => {
    event.preventDefault();
    const name = String(guestName || "").trim();
    const email = String(guestEmail || "").trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!name) {
      setLoadError("Укажите имя для входа в конференцию");
      return;
    }
    if (!emailOk) {
      setLoadError("Введите корректный email");
      return;
    }

    setSubmitting(true);
    setLoadError("");
    try {
      const payload = buildGuestJoinPayload(name, email);
      const joinedRoom = await sessionRoomApi.joinByInvite(normalizedToken, payload);
      const nextRoom = joinedRoom || null;
      setRoom(nextRoom);
      persistGuestDraft(normalizedToken, { name, email });

      if (!String(nextRoom?.aegisJoinUrl || nextRoom?.aegisPlayerUrl || "").trim()) {
        setLoadError("Конференция создана, но ссылка на плеер пока недоступна. Попробуйте еще раз через минуту.");
        return;
      }

      setJoined(true);
    } catch (error) {
      const status = Number(error?.status || 0);
      if (status === 401 || status === 403) {
        setLoadError("Ссылка требует доступа. Войдите в аккаунт или используйте актуальную ссылку приглашения.");
        return;
      }
      setLoadError(String(error?.message || "Не удалось подключиться к конференции"));
    } finally {
      setSubmitting(false);
    }
  }, [guestEmail, guestName, normalizedToken]);

  const handleCopyInvite = React.useCallback(async () => {
    if (!normalizedToken) return;
    try {
      await navigator.clipboard.writeText(toGuestInviteUrl(normalizedToken));
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 2200);
    } catch {
      setInviteCopied(false);
    }
  }, [normalizedToken]);

  return (
    <div className="conference-guest-page">
      <Helmet prioritizeSeoTags>
        <title>Вход в конференцию | Бюро Души</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="conference-guest-shell">
        <section className={`conference-guest-card ${joined ? "is-player" : "is-entry"}`}>
          {!joined ? (
            <>
              <aside className="conference-guest-side">
                <header className="conference-guest-head">
                  <Link className="conference-guest-brand" to="/">
                    <img src={logo} alt="Бюро Души" />
                  </Link>
                </header>

                <div className="conference-guest-overview">
                  <span className="conference-guest-kicker">{sessionLabel}</span>
                  <div className="conference-guest-title">
                    <h1>{roomTitle}</h1>
                  </div>
                  <div className="conference-guest-meta-chip">
                    <IconCalendar />
                    <span>{formatDateLabel(startAt)}</span>
                  </div>
                </div>

                <div className="conference-guest-host">
                  <div className="conference-guest-host__avatar" aria-hidden="true">
                    {psychologistAvatarUrl ? (
                      <img src={psychologistAvatarUrl} alt="" />
                    ) : (
                      <IconUserPlaceholder />
                    )}
                  </div>
                  <div className="conference-guest-host__text">
                    <strong>{psychologistName || "Психолог"}</strong>
                    <span>Психолог</span>
                  </div>
                </div>

                <div className="conference-guest-tip">
                  <p>Подключитесь за несколько минут до начала, чтобы проверить камеру и микрофон.</p>
                </div>

                <div className="conference-guest-share">
                  <button
                    type="button"
                    className="conference-guest-link-btn"
                    onClick={handleCopyInvite}
                    disabled={!normalizedToken}
                  >
                    <IconLink />
                    <span>{inviteCopied ? "Ссылка скопирована" : "Скопировать приглашение"}</span>
                  </button>
                </div>
              </aside>

              <section className="conference-guest-main">
                <div className="conference-guest-badges">
                  <span className="conference-inline-badge conference-inline-badge--mint">Aegis</span>
                </div>

                <div className="conference-guest-main__head">
                  <div className="conference-guest-title">
                    <h2>Подключиться к встрече</h2>
                    <p>Введите имя и email, чтобы присоединиться к сессии.</p>
                  </div>
                </div>

                <form className="conference-guest-form" onSubmit={onJoinAsGuest}>
                  <label className="conference-guest-field">
                    <span>Ваше имя</span>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(event) => setGuestName(event.target.value)}
                      placeholder="Например, Анна"
                      autoComplete="name"
                      maxLength={120}
                      required
                    />
                  </label>

                  <label className="conference-guest-field">
                    <span>Ваш email</span>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(event) => setGuestEmail(event.target.value)}
                      placeholder="name@example.com"
                      autoComplete="email"
                      maxLength={190}
                      required
                    />
                  </label>

                  {loadError ? <div className="conference-guest-error">{loadError}</div> : null}

                  <div className="conference-guest-actions">
                    <button type="submit" className="conference-primary-btn" disabled={submitting || !normalizedToken}>
                      {submitting ? "Подключаем..." : "Подключиться"}
                    </button>
                  </div>

                  <p className="conference-guest-switch">
                    Уже есть аккаунт?{" "}
                    <button
                      type="button"
                      className="conference-guest-switch__link"
                      onClick={() => navigate("/login", { state: { from: loginFrom } })}
                    >
                      {isAuthenticated ? "Открыть кабинет" : "Войти"}
                    </button>
                  </p>

                  <div className="conference-guest-privacy">
                    <div className="conference-guest-privacy__icon" aria-hidden="true">
                      <IconLock />
                    </div>
                    <p>
                      Мы используем ваши данные только для подключения к сессии и корректной идентификации участника в Aegis.
                    </p>
                  </div>

                  <p className="conference-guest-hint conference-guest-hint--dim">
                    Если вы психолог, используйте рабочую версию: <Link to={cabinetLink}>перейти в кабинет</Link>.
                  </p>
                </form>
              </section>
            </>
          ) : (
            <div className="conference-guest-player-wrap">
              {playerUrl ? (
                <iframe
                  title="Конференция Aegis"
                  src={playerUrl}
                  allow="camera; microphone; autoplay; fullscreen; display-capture"
                  allowFullScreen
                />
              ) : (
                <div className="conference-guest-error">Плеер пока недоступен. Попробуйте обновить страницу.</div>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
